import { Typography } from 'antd'
import { Button } from 'components/shared/Button'
import { INVENTORY_SEARCH_KEY } from 'constants/search'
import colors from 'helpers/colors'
import { useClassifications, useMaxPrice, useTechniques } from 'hooks/static'
import { omitBy } from 'lodash'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import styled from 'styled-components'

import { InventoryFilterFormItem } from './InventoryFilterFormItem'
import { InventoryFilterFormItemPrice } from './InventoryFilterFormItemPrice'
import { StyledCheckBox } from './StyledCheckBox'
import { StyledSearchInput } from './StyledSearchInput'

type Props = {
  onClose?: () => void
  xs?: boolean
  artists?: ArtistNameType[]
  pathname?: string
}

type FormValues = {
  [INVENTORY_SEARCH_KEY.keyword]: string
  [INVENTORY_SEARCH_KEY.artist]: string[]
  [INVENTORY_SEARCH_KEY.classification]: string[]
  [INVENTORY_SEARCH_KEY.technique]: string[]
  [INVENTORY_SEARCH_KEY.minPrice]?: number
  [INVENTORY_SEARCH_KEY.maxPrice]?: number
  [INVENTORY_SEARCH_KEY.displayAvailableOnly]: boolean
}

const normalizeArrayParam = (
  value: string | string[] | undefined,
): string[] => {
  if (!value) return []
  return Array.isArray(value) ? value : [value]
}

// Convert FormValues to query object for router.push
const formValuesToQuery = (values: FormValues) => {
  return omitBy(
    {
      [INVENTORY_SEARCH_KEY.keyword]: values[INVENTORY_SEARCH_KEY.keyword],
      [INVENTORY_SEARCH_KEY.artist]: values[INVENTORY_SEARCH_KEY.artist],
      [INVENTORY_SEARCH_KEY.classification]:
        values[INVENTORY_SEARCH_KEY.classification],
      [INVENTORY_SEARCH_KEY.technique]: values[INVENTORY_SEARCH_KEY.technique],
      [INVENTORY_SEARCH_KEY.minPrice]: values[INVENTORY_SEARCH_KEY.minPrice],
      [INVENTORY_SEARCH_KEY.maxPrice]: values[INVENTORY_SEARCH_KEY.maxPrice],
      [INVENTORY_SEARCH_KEY.displayAvailableOnly]: values[
        INVENTORY_SEARCH_KEY.displayAvailableOnly
      ]
        ? 'true'
        : undefined,
    },
    (value) =>
      value == null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0),
  )
}

export const InventoryFilterForm: FC<Props> = ({
  xs,
  onClose,
  artists,
  pathname,
}) => {
  const router = useRouter()
  const { t, i18n } = useTranslation()
  const language = i18n.language

  const { data: classifications } = useClassifications()
  const { data: techniques } = useTechniques()
  const { data: maxPrice } = useMaxPrice()

  // Get initial values from router query
  const getInitialValues = (): FormValues => {
    const query = router.query
    return {
      [INVENTORY_SEARCH_KEY.keyword]:
        (query[INVENTORY_SEARCH_KEY.keyword] as string) || '',
      [INVENTORY_SEARCH_KEY.artist]: normalizeArrayParam(
        query[INVENTORY_SEARCH_KEY.artist] as string | string[],
      ),
      [INVENTORY_SEARCH_KEY.classification]: normalizeArrayParam(
        query[INVENTORY_SEARCH_KEY.classification] as string | string[],
      ),
      [INVENTORY_SEARCH_KEY.technique]: normalizeArrayParam(
        query[INVENTORY_SEARCH_KEY.technique] as string | string[],
      ),
      [INVENTORY_SEARCH_KEY.minPrice]: query[INVENTORY_SEARCH_KEY.minPrice]
        ? Number(query[INVENTORY_SEARCH_KEY.minPrice])
        : undefined,
      [INVENTORY_SEARCH_KEY.maxPrice]: query[INVENTORY_SEARCH_KEY.maxPrice]
        ? Number(query[INVENTORY_SEARCH_KEY.maxPrice])
        : undefined,
      [INVENTORY_SEARCH_KEY.displayAvailableOnly]:
        query[INVENTORY_SEARCH_KEY.displayAvailableOnly] === 'true',
    }
  }

  const { control, handleSubmit, reset, getValues } = useForm<FormValues>({
    defaultValues: getInitialValues(),
  })

  const resetValues = () => {
    const resetValues = {
      [INVENTORY_SEARCH_KEY.keyword]: '',
      [INVENTORY_SEARCH_KEY.artist]: [],
      [INVENTORY_SEARCH_KEY.classification]: [],
      [INVENTORY_SEARCH_KEY.technique]: [],
      [INVENTORY_SEARCH_KEY.minPrice]: undefined,
      [INVENTORY_SEARCH_KEY.maxPrice]: undefined,
      [INVENTORY_SEARCH_KEY.displayAvailableOnly]: false,
    }
    reset(resetValues)
    // Clear URL params after resetting form
    if (!xs) {
      updateQuery(resetValues)
    }
  }

  const updateQuery = (values: FormValues) => {
    const query = formValuesToQuery(values)

    // Preserve hash if it exists (extract from asPath)
    const hash = router.asPath.includes('#')
      ? router.asPath.substring(router.asPath.indexOf('#'))
      : ''

    router.push(
      {
        pathname,
        query,
        hash,
      },
      undefined,
      { shallow: true },
    )
  }

  const onSubmit = (data: FormValues) => {
    updateQuery(data)
    onClose?.()
  }

  // Update URL query on desktop
  const updateUrlQuery = (fieldName: keyof FormValues, value: unknown) => {
    if (!xs) {
      const currentValues = getValues()
      const values = { ...currentValues, [fieldName]: value }
      const query = formValuesToQuery(values)

      // Preserve hash if it exists (extract from asPath)
      const hash = router.asPath.includes('#')
        ? router.asPath.substring(router.asPath.indexOf('#'))
        : ''

      router.push(
        {
          pathname,
          query,
          hash,
        },
        undefined,
        { shallow: true },
      )
    }
  }

  return (
    <form
      id={'filter-top'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: xs ? '100%' : '262px',
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      {xs && (
        <FixedWrapper
          style={{
            position: 'sticky',
            top: 0,
            justifyContent: 'space-between',
            borderBottom: `1px solid ${colors.grey}`,
          }}
        >
          <a onClick={onClose} style={{ textDecorationLine: 'underline' }}>
            {t('キャンセル')}
          </a>
          <Button
            size="sm"
            type="secondary"
            onClick={resetValues}
            style={{ padding: '12px 16px' }}
          >
            {t('条件をクリア')}
          </Button>
        </FixedWrapper>
      )}

      {!xs && (
        <Typography.Text
          style={{ fontSize: '16px', padding: '16px 0px', lineHeight: 1 }}
          strong
        >
          {t('フィルター')}
        </Typography.Text>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: xs ? '40px 16px' : 0,
        }}
      >
        <Controller
          name={INVENTORY_SEARCH_KEY.displayAvailableOnly}
          control={control}
          render={({ field }) => (
            <StyledCheckBox
              style={{ paddingBottom: '20px' }}
              checked={field.value}
              onChange={(e) => {
                const checked = e.target.checked
                field.onChange(checked)
                updateUrlQuery(
                  INVENTORY_SEARCH_KEY.displayAvailableOnly,
                  checked,
                )
              }}
            >
              {t('販売中のみ')}
            </StyledCheckBox>
          )}
        />
        <div style={{ padding: '28px 0' }}>
          <Typography.Text
            style={{
              display: 'flex',
              fontSize: '16px',
              lineHeight: 1,
              paddingBottom: '20px',
            }}
          >
            {t('キーワード')}
          </Typography.Text>
          <Controller
            name={INVENTORY_SEARCH_KEY.keyword}
            control={control}
            render={({ field }) => (
              <StyledSearchInput
                {...field}
                placeholder={t('検索ワードを入力')}
                allowClear
                onChange={(e) => {
                  field.onChange(e)
                  updateUrlQuery(INVENTORY_SEARCH_KEY.keyword, e.target.value)
                }}
              />
            )}
          />
        </div>
        {artists && (
          <Controller
            name={INVENTORY_SEARCH_KEY.artist}
            control={control}
            render={({ field }) => (
              <InventoryFilterFormItem
                title={t('アーティスト')}
                language={language}
                selectableObjects={artists}
                checkedIds={field.value}
                setCheckedIds={(value) => {
                  field.onChange(value)
                  updateUrlQuery(INVENTORY_SEARCH_KEY.artist, value)
                }}
              />
            )}
          />
        )}
        {classifications && (
          <Controller
            name={INVENTORY_SEARCH_KEY.classification}
            control={control}
            render={({ field }) => (
              <InventoryFilterFormItem
                title={t('分類')}
                language={language}
                selectableObjects={classifications}
                checkedIds={field.value}
                setCheckedIds={(value) => {
                  field.onChange(value)
                  updateUrlQuery(INVENTORY_SEARCH_KEY.classification, value)
                }}
              />
            )}
          />
        )}
        {techniques && (
          <Controller
            name={INVENTORY_SEARCH_KEY.technique}
            control={control}
            render={({ field }) => (
              <InventoryFilterFormItem
                title={t('技法')}
                language={language}
                selectableObjects={techniques}
                checkedIds={field.value}
                setCheckedIds={(value) => {
                  field.onChange(value)
                  updateUrlQuery(INVENTORY_SEARCH_KEY.technique, value)
                }}
                searchable
              />
            )}
          />
        )}
        <Controller
          name={INVENTORY_SEARCH_KEY.minPrice}
          control={control}
          render={({ field: minField }) => (
            <Controller
              name={INVENTORY_SEARCH_KEY.maxPrice}
              control={control}
              render={({ field: maxField }) => (
                <InventoryFilterFormItemPrice
                  maxLimit={maxPrice || 10000000}
                  min={minField.value}
                  max={maxField.value}
                  setMin={(value) => {
                    minField.onChange(value)
                    updateUrlQuery(INVENTORY_SEARCH_KEY.minPrice, value)
                  }}
                  setMax={(value) => {
                    maxField.onChange(value)
                    updateUrlQuery(INVENTORY_SEARCH_KEY.maxPrice, value)
                  }}
                />
              )}
            />
          )}
        />
        <div style={{ paddingBottom: '60px' }} />
      </div>
      {xs && (
        <FixedWrapper
          style={{
            position: 'fixed',
            bottom: 0,
            justifyContent: 'center',
          }}
        >
          <Button
            size="md"
            type="primary"
            htmlType="submit"
            style={{
              display: 'flex',
              width: '100%',
              padding: '16px 24px',
              fontSize: '16px',
              fontWeight: 400,
            }}
          >
            {t('結果を表示する')}
          </Button>
        </FixedWrapper>
      )}
    </form>
  )
}

const FixedWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  background: white;
  padding: 16px;
  z-index: 5;
  width: 100vw;
`
